import React, { useEffect, useRef, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Upload, Loader2, PlayCircle, Trash2, Film, X, Link2, Image as ImageIcon, Settings2 } from 'lucide-react';
import { coachVideosAPI, uploadAPI, mediaUrl } from '../services/api';
import { VideoView, detectProvider } from './VideoView';

const VideoSlot = ({ label, type, day, current, onDone }) => {
  const [mode, setMode] = useState('file'); // 'file' | 'link'
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [link, setLink] = useState('');
  const [savingLink, setSavingLink] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [savingInfo, setSavingInfo] = useState(false);
  const [thumbUploading, setThumbUploading] = useState(false);
  const [meta, setMeta] = useState({ title: '', description: '', allow_download: false, active: true, thumbnail_url: null });
  const ref = useRef(null);
  const thumbRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    setMeta({
      title: current?.title || '',
      description: current?.description || '',
      allow_download: !!current?.allow_download,
      active: current?.active !== false,
      thumbnail_url: current?.thumbnail_url || null,
    });
  }, [current]);

  const buildMeta = () => ({
    title: meta.title || null,
    description: meta.description || null,
    allow_download: !!meta.allow_download,
    active: !!meta.active,
    thumbnail_url: meta.thumbnail_url || null,
  });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video')) { toast.error('Choisis un fichier vidéo (mp4, mov, webm...)'); return; }
    setUploading(true);
    setProgress(0);
    abortRef.current = new AbortController();
    try {
      const res = await uploadAPI.upload(file, { signal: abortRef.current.signal, onProgress: setProgress });
      await coachVideosAPI.set({ type, day_number: day ?? null, url: res.url, media_type: 'video', provider: 'file', ...buildMeta() });
      toast.success('Vidéo enregistrée');
      onDone?.();
    } catch (err) {
      if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') toast('Téléversement annulé');
      else toast.error(err?.response?.data?.detail || "Échec de l'envoi (format ou taille non conforme ?)");
    } finally {
      setUploading(false); setProgress(0);
      if (ref.current) ref.current.value = '';
    }
  };

  const cancelUpload = () => { abortRef.current?.abort(); };

  const saveLink = async () => {
    const url = link.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) { toast.error('Colle un lien valide (https://...)'); return; }
    const provider = detectProvider(url); // youtube | vimeo | link
    setSavingLink(true);
    try {
      await coachVideosAPI.set({ type, day_number: day ?? null, url, media_type: 'video', provider, ...buildMeta() });
      toast.success(provider === 'link' ? 'Lien enregistré (bouton « Voir la vidéo »)' : 'Lien vidéo enregistré');
      setLink('');
      onDone?.();
    } catch { toast.error('Échec'); }
    finally { setSavingLink(false); }
  };

  const saveInfo = async () => {
    if (!current?.url) return;
    setSavingInfo(true);
    try {
      await coachVideosAPI.set({ type, day_number: day ?? null, url: current.url, media_type: 'video', provider: current.provider || detectProvider(current.url), ...buildMeta() });
      toast.success('Infos enregistrées');
      onDone?.();
    } catch { toast.error('Échec'); }
    finally { setSavingInfo(false); }
  };

  const uploadThumb = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setThumbUploading(true);
    try {
      const res = await uploadAPI.upload(file);
      setMeta((m) => ({ ...m, thumbnail_url: res.url }));
      toast.success('Miniature ajoutée (pense à Enregistrer)');
    } catch { toast.error("Échec de l'envoi"); }
    finally { setThumbUploading(false); if (thumbRef.current) thumbRef.current.value = ''; }
  };

  const remove = async () => {
    if (!window.confirm('Supprimer définitivement cette vidéo ?')) return;
    try {
      await coachVideosAPI.remove(type, day ?? undefined);
      toast.success('Vidéo supprimée');
      onDone?.();
    } catch { toast.error('Échec de la suppression'); }
  };

  const slotId = `${type}${day ? '-' + day : ''}`;
  const isFileVideo = current?.url && (current.provider || detectProvider(current.url)) === 'file';

  return (
    <div className="rounded-xl border border-border p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-foreground">{label}</span>
        <div className="flex items-center gap-2">
          {current?.url && current.active === false && <span className="text-[10px] font-semibold text-slate-500">Masquée</span>}
          {current?.url && (
            <button onClick={remove} className="text-red-600" data-testid={`del-video-${slotId}`}><Trash2 className="w-4 h-4" /></button>
          )}
        </div>
      </div>

      {current?.url ? (
        <VideoView video={current} className="w-full aspect-video rounded-lg bg-black mb-2" />
      ) : (
        <div className="w-full aspect-video rounded-lg bfc-dark flex items-center justify-center mb-2">
          <Film className="w-8 h-8 text-neutral-600" />
        </div>
      )}

      {/* mode toggle */}
      <div className="flex gap-1 p-1 bg-muted rounded-full mb-2">
        <button onClick={() => setMode('file')} data-testid={`mode-file-${slotId}`} className={`flex-1 py-1.5 rounded-full text-xs font-semibold flex items-center justify-center gap-1 ${mode === 'file' ? 'bg-white dark:bg-red-900 text-red-700 dark:text-red-200 shadow-sm' : 'text-muted-foreground'}`}><Upload className="w-3.5 h-3.5" />Fichier</button>
        <button onClick={() => setMode('link')} data-testid={`mode-link-${slotId}`} className={`flex-1 py-1.5 rounded-full text-xs font-semibold flex items-center justify-center gap-1 ${mode === 'link' ? 'bg-white dark:bg-red-900 text-red-700 dark:text-red-200 shadow-sm' : 'text-muted-foreground'}`}><Link2 className="w-3.5 h-3.5" />Lien</button>
      </div>

      {mode === 'file' ? (
        uploading ? (
          <div>
            <div className="w-full h-2 rounded-full bg-muted overflow-hidden mb-2">
              <div className="h-full bg-red-600 transition-all" style={{ width: `${progress}%` }} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">{progress}%</span>
              <button onClick={cancelUpload} data-testid={`cancel-upload-${slotId}`} className="text-xs font-semibold text-red-600 flex items-center gap-1"><X className="w-3.5 h-3.5" />Annuler</button>
            </div>
          </div>
        ) : (
          <button onClick={() => ref.current?.click()} data-testid={`upload-video-${slotId}`}
            className="w-full py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold flex items-center justify-center gap-2">
            <Upload className="w-4 h-4" />{current?.url ? 'Remplacer par un fichier' : 'Uploader un fichier'}
          </button>
        )
      ) : (
        <div className="flex gap-2">
          <Input value={link} onChange={(e) => setLink(e.target.value)} data-testid={`link-input-${slotId}`} placeholder="Lien YouTube, Vimeo ou réseaux sociaux" className="rounded-lg text-sm" />
          <Button onClick={saveLink} disabled={savingLink} data-testid={`save-link-${slotId}`} className="bg-orange-500 hover:bg-orange-600 rounded-lg shrink-0">
            {savingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : 'OK'}
          </Button>
        </div>
      )}
      <input ref={ref} type="file" accept="video/*" className="hidden" onChange={handleFile} />

      {/* Options / metadata */}
      <button onClick={() => setShowOptions((s) => !s)} data-testid={`options-toggle-${slotId}`} className="mt-3 text-xs font-semibold text-muted-foreground flex items-center gap-1">
        <Settings2 className="w-3.5 h-3.5" />{showOptions ? 'Masquer les infos' : 'Titre, description, options'}
      </button>
      {showOptions && (
        <div className="mt-2 space-y-2 border-t border-border pt-3">
          <div className="space-y-1"><Label className="text-xs">Titre</Label><Input value={meta.title} onChange={(e) => setMeta((m) => ({ ...m, title: e.target.value }))} data-testid={`video-title-${slotId}`} className="rounded-lg text-sm" placeholder="Titre de la vidéo" /></div>
          <div className="space-y-1"><Label className="text-xs">Description</Label><Textarea value={meta.description} onChange={(e) => setMeta((m) => ({ ...m, description: e.target.value }))} data-testid={`video-desc-${slotId}`} rows={2} className="rounded-lg text-sm resize-none" placeholder="Courte description" /></div>
          <div className="flex items-center gap-2">
            <div className="w-14 h-10 rounded bg-muted overflow-hidden flex items-center justify-center shrink-0">
              {meta.thumbnail_url ? <img src={mediaUrl(meta.thumbnail_url)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 text-muted-foreground" />}
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => thumbRef.current?.click()} disabled={thumbUploading} data-testid={`thumb-upload-${slotId}`} className="rounded-full text-xs">
              {thumbUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Miniature'}
            </Button>
            <input ref={thumbRef} type="file" accept="image/*" className="hidden" onChange={uploadThumb} />
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-xs">Téléchargement autorisé (fichiers)</Label>
            <Switch checked={!!meta.allow_download} onCheckedChange={(v) => setMeta((m) => ({ ...m, allow_download: v }))} data-testid={`allow-download-${slotId}`} />
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-xs">Visible par les membres</Label>
            <Switch checked={!!meta.active} onCheckedChange={(v) => setMeta((m) => ({ ...m, active: v }))} data-testid={`video-active-${slotId}`} />
          </div>
          {current?.url && (
            <Button onClick={saveInfo} disabled={savingInfo} data-testid={`save-info-${slotId}`} className="w-full bg-orange-500 hover:bg-orange-600 rounded-full text-sm font-semibold">
              {savingInfo ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer les infos'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export const CoachVideos = () => {
  const [list, setList] = useState([]);
  const [day, setDay] = useState(1);

  const load = () => coachVideosAPI.list().then(setList).catch(() => {});
  useEffect(() => { load(); }, []);

  const find = (type, d = null) => list.find((v) => v.type === type && (v.day_number ?? null) === d);

  return (
    <div className="space-y-6">
      <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50">
        <h3 className="font-bold text-red-950 dark:text-red-50 mb-1 flex items-center gap-2"><PlayCircle className="w-5 h-5 text-red-600" />Vidéo de présentation (landing)</h3>
        <p className="text-xs text-muted-foreground mb-3">Fichier téléversé ou lien (YouTube, Vimeo, réseaux sociaux). Visible par tous les visiteurs.</p>
        <div className="max-w-sm">
          <VideoSlot label="Présentation du challenge" type="landing" day={null} current={find('landing', null)} onDone={load} />
        </div>
      </Card>

      <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50">
        <h3 className="font-bold text-red-950 dark:text-red-50 mb-1 flex items-center gap-2"><Film className="w-5 h-5 text-red-600" />Vidéos par jour</h3>
        <p className="text-xs text-muted-foreground mb-3">Séance sport + motivation, débloquées selon la progression de chaque challenger.</p>
        <div className="flex items-center gap-2 mb-4 overflow-x-auto no-scrollbar">
          {Array.from({ length: 21 }, (_, i) => i + 1).map((d) => (
            <button key={d} onClick={() => setDay(d)} data-testid={`coach-day-${d}`}
              className={`w-9 h-9 shrink-0 rounded-lg text-sm font-bold ${day === d ? 'bg-red-600 text-white' : 'bg-muted text-muted-foreground'}`}>
              {d}
            </button>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <VideoSlot label={`Formation nutrition — Jour ${day}`} type="nutrition" day={day} current={find('nutrition', day)} onDone={load} />
          <VideoSlot label={`Séance sport — Jour ${day}`} type="session" day={day} current={find('session', day)} onDone={load} />
          <VideoSlot label={`Motivation — Jour ${day}`} type="motivation" day={day} current={find('motivation', day)} onDone={load} />
        </div>
      </Card>
    </div>
  );
};
